/**
 * Tenant-user TanStack Query hooks — Phase 3.6.B.
 *
 * Pairs with BE routes:
 *   GET    /api/users                            → list
 *   POST   /api/users/invite                     → invite (creates Cognito + DB row)
 *   PATCH  /api/users/{id}                       → edit (name, title, role, phone)
 *   POST   /api/users/{id}/deactivate            → soft-deactivate
 *   POST   /api/users/{id}/reactivate            → restore
 *
 * Defensive empty-state behavior: if the BE route is not deployed yet,
 * the list query returns `[]` (caught at queryFn) so the panel renders
 * its empty state instead of crashing. Mutations bubble errors so the
 * toast surfaces them.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiClient } from '@/api/client'
import { BROWSABLE_CHANNELS_QUERY_KEY } from '@/features/chat/hooks/use-browsable-channels'
import { CONVERSATIONS_QUERY_KEY } from '@/features/chat/hooks/use-conversations'

import { ORGANIZATION_QUERY_KEY } from './use-organization'

import type { InviteUserInput, TenantUser, UpdateUserInput } from './types'

export const TENANT_USERS_QUERY_KEY = ['tenant-users'] as const

/**
 * BE response row — the actual on-the-wire shape from `/api/users`.
 * It does NOT match `TenantUser` 1:1 (the FE was specced against a
 * `roleSlug`/`roleName`/`active` shape; the BE serializer emits
 * `role`/`status` per RBAC slug + soft-delete pattern). We bridge in
 * the queryFn so consumers see the expected shape.
 *
 * Once the BE serializer is widened to also emit `roleSlug`/`roleName`/
 * `active`/`phone` (or vice versa), this bridge collapses to a pure
 * pass-through and can be deleted.
 */
interface BackendTenantUserRow {
  id: string
  email: string
  name: string
  title: string | null
  avatarUrl: string | null
  timezone: string | null
  role: string
  status: 'active' | 'deactivated'
  deactivatedAt: string | null
  lastLoginAt: string | null
  joinedAt: string
  createdAt: string
  /** New in 3.6.D — true when user has not yet accepted any invitation in this tenant. */
  pendingInvite?: boolean
  /** Guest's assigned chat channel; absent on a pre-per-guest BE. */
  guestChannel?: { id: string; name: string; isPrivate: boolean } | null
  kind?: 'human' | 'agent'
}

interface ListResponse {
  data: BackendTenantUserRow[]
}

/**
 * Display labels for role slugs. This must cover EVERY slug in the role
 * catalog: `roleName` falls back to the raw slug, and it is also the sort
 * key and the search haystack in the User Management table — so a missing
 * entry renders a lowercase slug that sorts and filters as one.
 *
 * `developer` was removed here because the slug itself is gone (renamed to
 * `manager`); `manager` / `member` had been missing since that rename.
 */
const ROLE_NAME_OVERRIDES: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
  commenter: 'Commenter',
  reader: 'Reader',
}

const fromBackend = (row: BackendTenantUserRow): TenantUser => ({
  id: row.id,
  email: row.email,
  name: row.name,
  title: row.title,
  avatarUrl: row.avatarUrl,
  // BE → FE shape bridge:
  //   - `role` (slug) becomes both `roleSlug` (canonical) and the
  //     fallback for `roleName` if the slug isn't in the override map
  //   - `status === 'active'` becomes the boolean `active`
  //   - `phone` isn't in the BE response yet — null until it lands
  roleSlug: row.role,
  roleName: ROLE_NAME_OVERRIDES[row.role] ?? row.role,
  active: row.status === 'active',
  createdAt: row.createdAt,
  lastLoginAt: row.lastLoginAt,
  phone: null,
  // Default to false on older BE responses (pre-3.6.D); the new BE
  // always sends a boolean. Once every environment has rolled the new
  // BE this fallback can drop.
  pendingInvite: row.pendingInvite ?? false,
  timezone: row.timezone,
  guestChannel: row.guestChannel ?? null,
  kind: row.kind ?? 'human',
})

export function useTenantUsers() {
  return useQuery({
    queryKey: TENANT_USERS_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await apiClient<ListResponse>('/api/users')
        return (res.data ?? []).map(fromBackend)
      } catch (err) {
        // BE route not deployed yet → render empty state defensively.
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return [] as TenantUser[]
        }
        throw err
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useInviteUser() {
  const queryClient = useQueryClient()
  return useMutation<TenantUser, Error, InviteUserInput>({
    mutationFn: async (input) => {
      // Bridge FE shape → BE shape. FE carries `roleSlug`; BE schema
      // requires `role` (z.enum(ROLES)). Without this remap the BE
      // rejects with 400 because `role` is missing. Mirrors the same
      // bridge in `useUpdateUser` below.
      const { roleSlug, ...rest } = input
      const body: Record<string, unknown> = { ...rest, role: roleSlug }
      const res = await apiClient<{ data: TenantUser }>('/api/users/invite', {
        method: 'POST',
        body,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY })
      // A guest invite consumes a seat and may join a channel.
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: BROWSABLE_CHANNELS_QUERY_KEY })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation<TenantUser, Error, { id: string; input: UpdateUserInput }>({
    mutationFn: async ({ id, input }) => {
      // Bridge FE shape → BE shape. The FE specced `roleSlug` (matching
      // the rest of the role-related fields the panel reads — `roleSlug`
      // / `roleName`); the BE PATCH input expects `role`. Without this
      // remap the BE's Zod schema silently strips the unknown field and
      // the PATCH returns 200 with no role change applied.
      const { roleSlug, ...rest } = input
      const body: Record<string, unknown> = { ...rest }
      if (roleSlug !== undefined) body.role = roleSlug
      const res = await apiClient<{ data: TenantUser }>(`/api/users/${id}`, {
        method: 'PATCH',
        body,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY })
      // Role / channel changes move channel membership and the guest seat
      // count.
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: BROWSABLE_CHANNELS_QUERY_KEY })
    },
  })
}

export function useToggleUserActive() {
  const queryClient = useQueryClient()
  return useMutation<TenantUser, Error, { id: string; nextActive: boolean }>({
    mutationFn: async ({ id, nextActive }) => {
      const path = nextActive ? `/api/users/${id}/reactivate` : `/api/users/${id}/deactivate`
      const res = await apiClient<{ data: TenantUser }>(path, { method: 'POST' })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_USERS_QUERY_KEY })
    },
  })
}

/**
 * Resend the magic-link invitation email — cancels any pending tokens
 * for this user in the tenant and issues a fresh one. Only meaningful
 * for users who haven't completed first sign-in (the BE returns 409
 * if the user has already accepted).
 */
export function useResendInvite() {
  return useMutation<{ emailSent: boolean }, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      const res = await apiClient<{ data: { emailSent: boolean } }>(
        `/api/users/${id}/resend-invite`,
        { method: 'POST' },
      )
      return res.data
    },
  })
}
